import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  Document,
  Page,
  Text as PdfText,
  View,
  StyleSheet,
  Font,
  Image,
  Link,
} from '@react-pdf/renderer';

import { ProjectElementType, TextBlockType, DisplaySize, AboutContentMode, CareerType, FormationImageMode, normalizePEInfoType, PEInfoType, usesWorkDuration } from '../../shared/enum/EnumRepository';
import {
  ProjectElementData,
  ProjectInfoData,
  TextBlockData,
  ProjectElementDataWithText,
  ProjectElementDataWithWork,
  ProjectElementDataWithDetail,
  ProjectElementDataWithDocument,
  ProjectElementDataWithPDF,
  ProjectElementDataWithTextBox,
  ProjectElementDataWithFormation,
  FormationItemData,
  convertWorkSizeToString,
  MemberInfoData,
  CareerData,
} from '../../shared/dto/EntityRepository';
import { HorizontalCVPdfPage } from '../about/DocumentHorizontalCV';
import { convertS3UrlToCloudFrontUrl } from '../../shared/aws/awsHelper';
import { resolveProjectImageUrls } from '../../shared/util/pdfImageUtils';
import { getProjectInfoDisplayName } from '../../shared/util/projectInfo';
import {
  formatFormationItemInfo,
  getFormationImageMode,
  getFormationImagePosition,
  getVisibleFormationItems,
  resolveFormationSlotImage,
} from '../../shared/util/formationUtils';
import {
  FORMATION_INFO_ROW_GAP,
  FormationPdfSlotDimensions,
  getFormationPdfInfoPosition,
  getFormationPdfLayout,
} from '../projectElement/formation/formationGrid';
import logo from '../../asset/gnb/logo_qr_transparent.png';
import pdfIcon from '../../asset/icon/pdf.png';
import { resolveWorkArtistName } from '../../shared/util/workArtist';
import { parseSupportedVideoUrl } from '../../shared/util/videoUrl';
import { parseSupportedTextBlockLinkUrl } from '../../shared/util/textBlockLinkUrl';
import { RICH_TEXT_LINE_HEIGHT, richTextToParagraphs } from '../../shared/richText/document';
import { getTextBlockLinkCaption } from '../../shared/util/textBlockLinkCaption';

const RE = {
  HANGUL: /[\uAC00-\uD7A3]/,
  KANA: /[\u3040-\u30FF\u31F0-\u31FF]/,
  HAN: /[\u3400-\u9FFF\uF900-\uFAFF]/,
};

function pickFont(chunk: string) {
  if (RE.KANA.test(chunk)) return 'NotoJP';
  if (RE.HANGUL.test(chunk)) return 'NotoKR';
  if (RE.HAN.test(chunk)) return 'NotoSC';
  return 'NotoKR';
}

const Text = ({ children, style, render, ...rest }: any) => {
  if (typeof render === 'function') {
    return <PdfText style={style} render={render} {...rest} />;
  }

  if (typeof children !== 'string' && typeof children !== 'number') {
    return (
      <PdfText style={style} {...rest}>
        {children}
      </PdfText>
    );
  }

  const s = String(children);

  if (!RE.HANGUL.test(s) && !RE.KANA.test(s) && !RE.HAN.test(s)) {
    return (
      <PdfText style={style} {...rest}>
        {children}
      </PdfText>
    );
  }

  const parts = s
    .split(/([\uAC00-\uD7A3]+|[\u3040-\u30FF\u31F0-\u31FF]+|[\u3400-\u9FFF\uF900-\uFAFF]+)/)
    .filter(Boolean);

  return (
    <PdfText style={style} {...rest}>
      {parts.map((p, i) => (
        <PdfText key={i} style={[style, { fontFamily: pickFont(p) }]}>
          {p}
        </PdfText>
      ))}
    </PdfText>
  );
};

// Register Noto font
Font.register({
  family: 'NotoKR',
  fonts: [
    { src: '/fonts/NotoSansKR-Regular.ttf', fontWeight: 400 },
    { src: '/fonts/NotoSansKR-Medium.ttf', fontWeight: 500 },
    { src: '/fonts/NotoSansKR-SemiBold.ttf', fontWeight: 600 },
  ],
});

Font.register({
  family: 'NotoJP',
  fonts: [
    { src: '/fonts/NotoSansJP-Regular.ttf', fontWeight: 400 },
    { src: '/fonts/NotoSansJP-Medium.ttf', fontWeight: 500 },
    { src: '/fonts/NotoSansJP-SemiBold.ttf', fontWeight: 600 },
  ],
});

Font.register({
  family: 'NotoSC',
  fonts: [
    { src: '/fonts/NotoSansSC-Regular.ttf', fontWeight: 400 },
    { src: '/fonts/NotoSansSC-Medium.ttf', fontWeight: 500 },
    { src: '/fonts/NotoSansSC-SemiBold.ttf', fontWeight: 600 },
  ],
});

const A4_LANDSCAPE_WIDTH = 842;
const A4_LANDSCAPE_HEIGHT = 595;
const PAGE_HORIZONTAL_PADDING = 60;
const PAGE_VERTICAL_PADDING = 60;
const FORMATION_WITH_INFO_OFFSET_Y = 6;
const TEXT_COLUMN_HORIZONTAL_PADDING = 30;
const TEXT_BLOCK_IMAGE_WIDTH_RATIO = 0.8;
const TEXT_BLOCK_COLUMN_WIDTH =
  (A4_LANDSCAPE_WIDTH - PAGE_HORIZONTAL_PADDING) / 2 - TEXT_COLUMN_HORIZONTAL_PADDING;
const TEXT_BLOCK_IMAGE_MAX_SIZE = TEXT_BLOCK_COLUMN_WIDTH * TEXT_BLOCK_IMAGE_WIDTH_RATIO;
const TEXT_PARAGRAPH_FONT_SIZE = 9;
const TEXT_PARAGRAPH_LINE_HEIGHT = 15;
const TEXT_PARAGRAPH_LINE_HEIGHT_RATIO =
  TEXT_PARAGRAPH_LINE_HEIGHT / TEXT_PARAGRAPH_FONT_SIZE;
const TEXT_BLOCK_DISTANCE = 56;
const TEXT_BLOCK_GAP =
  TEXT_BLOCK_DISTANCE - TEXT_PARAGRAPH_LINE_HEIGHT;

interface DocumentProjectProps {
  title: string;
  username: string;
  includeArtist: boolean;
  projectInfoList: ProjectInfoData[];
  projectElementList: ProjectElementData[];
  /** 미리 해석된 URL 맵 (PDF toBlob 시 첫 렌더부터 사용, 모달에서 전달) */
  resolvedImageUrlMap?: Record<string, string>;
  resolvedGifFirstFrameMap?: Record<string, string>;
  /** 프로젝트 PDF 마지막에 CV 페이지 포함 */
  includeCv?: boolean;
  /** CV 포함 시 원문/영문 */
  cvContentMode?: AboutContentMode;
  /** 공개 API 기준 멤버 정보 (includeCv일 때 주입) */
  cvMemberInfo?: MemberInfoData | null;
  /** 공개 경력만 (includeCv일 때 주입) */
  cvCareerList?: CareerData[];
  /** About에 설정된 CareerType 섹션 순서 */
  careerSectionOrder?: readonly CareerType[];
}

const pickPdfFontSize = (style: any): number | undefined => {
  if (!style) return undefined;
  if (Array.isArray(style)) {
    for (let i = 0; i < style.length; i += 1) {
      if (style[i] && typeof style[i].fontSize === 'number') {
        return style[i].fontSize;
      }
    }
    return undefined;
  }
  return typeof style.fontSize === 'number' ? style.fontSize : undefined;
};

const renderRichPdfText = (
  value: string,
  style: any,
  mediumLineHeight = RICH_TEXT_LINE_HEIGHT.M,
) => {
  const paragraphs = richTextToParagraphs(value);
  const baseSize = pickPdfFontSize(style);
  return paragraphs.map((paragraph, paragraphIndex) => {
    const paragraphStyle = [
      ...(Array.isArray(style) ? style : [style]),
      { lineHeight: 1 },
      ...(paragraph.textAlign ? [{ textAlign: paragraph.textAlign }] : []),
    ];

    return (
      <Text key={paragraphIndex} style={paragraphStyle}>
        {paragraph.runs.length === 0
          ? <Text style={{ fontSize: baseSize, lineHeight: mediumLineHeight }}>{' '}</Text>
          : paragraph.runs.map((run, runIndex) => (
            <Text
              key={runIndex}
              style={{
                fontWeight: run.bold ? 600 : undefined,
                transform: run.italic ? 'skewX(-12deg)' : undefined,
                textDecoration: run.underline ? 'underline' : undefined,
                lineHeight: run.sizeS
                  ? RICH_TEXT_LINE_HEIGHT.S
                  : run.sizeL
                    ? RICH_TEXT_LINE_HEIGHT.L
                    : mediumLineHeight,
                fontSize: baseSize
                  ? run.sizeS
                    ? baseSize - 2
                    : run.sizeL
                      ? baseSize + 2
                      : baseSize
                  : undefined,
              }}
            >
              {run.text}
            </Text>
          ))}
      </Text>
    );
  });
};

// 텍스트 블록 렌더링 함수
const renderTextBlock = (block: TextBlockData, getImageUrl: GetImageUrlFn) => {
  switch (block.type) {
    case TextBlockType.PARAGRAPH: {
      return renderRichPdfText(
        block.textContent,
        PDFStyles.Text.textParagraph,
        TEXT_PARAGRAPH_LINE_HEIGHT_RATIO,
      );
    }
    case TextBlockType.IMAGE:
      if (!block.uploadFile?.originUrl) {
        return null;
      }

      const { width, height } = calculateTextBlockImageSize(
        block.uploadFile.width || 1,
        block.uploadFile.height || 1
      );

      return (
        <View style={PDFStyles.Section.textBlockImageContainer} wrap={false}>
          <View style={PDFStyles.Section.textBlockImageWrapper}>
            <Image
              source={getImageUrl(block.uploadFile.originUrl)}
              style={[
                PDFStyles.Image.textBlockImage,
                {
                  width,
                  height,
                },
              ]}
            />
          </View>
          {block.caption && (
            <Text style={PDFStyles.Text.textBlockImageCaption}>{block.caption}</Text>
          )}
        </View>
      );
    case TextBlockType.LINK: {
      const label = getTextBlockLinkCaption(block) || block.url || "";
      if (!label) return null;
      const parsedLink = parseSupportedTextBlockLinkUrl(block.url || "");
      const text = <Text style={PDFStyles.Text.textLink}>{label}</Text>;
      return parsedLink
        ? <Link src={parsedLink.url.href} style={PDFStyles.Link.textBlock}>{text}</Link>
        : text;
    }
    default:
      return null;
  }
};

// 이미지 크기 계산 함수 (DisplaySize 적용)
const calculateConstrainedImageSize = (
  imageWidth: number,
  imageHeight: number,
  maxWidth: number,
  maxHeight: number
): { width: number; height: number } => {
  const imageAspectRatio = imageWidth / imageHeight;
  const maxAspectRatio = maxWidth / maxHeight;

  if (imageAspectRatio > maxAspectRatio) {
    return {
      width: maxWidth,
      height: maxWidth / imageAspectRatio,
    };
  }

  return {
    width: maxHeight * imageAspectRatio,
    height: maxHeight,
  };
};

const calculateImageSize = (
  imageWidth: number,
  imageHeight: number,
  displaySize: DisplaySize
): { width: number; height: number } => {
  // DisplaySize에 따른 비율
  const sizeMultiplier = {
    [DisplaySize.WIDE]: 0.90,
    [DisplaySize.BIG]: 0.80,
    [DisplaySize.MEDIUM]: 0.70,
    [DisplaySize.REGULAR]: 0.60,
    [DisplaySize.SMALL]: 0.50,
  }[displaySize] || 0.70;
  
  // 사용 가능한 영역 (패딩 제외)
  const availableWidth = A4_LANDSCAPE_WIDTH - PAGE_HORIZONTAL_PADDING; // 742pt
  const availableHeight = A4_LANDSCAPE_HEIGHT - PAGE_VERTICAL_PADDING; // 495pt

  return calculateConstrainedImageSize(
    imageWidth,
    imageHeight,
    availableWidth * sizeMultiplier,
    availableHeight * sizeMultiplier
  );
};

const WORK_CAPTION_FONT_SIZE = 8;

const estimatePdfCaptionWidth = (text: string, fontSize = WORK_CAPTION_FONT_SIZE): number => {
  let width = 0;
  for (const char of text) {
    const code = char.charCodeAt(0);
    if (char === ' ') width += fontSize * 0.3;
    else if (code > 0x2e80) width += fontSize * 1;
    else width += fontSize * 0.55;
  }
  return width;
};

type GetImageUrlFn = (originUrl: string) => string;

const calculateTextBlockImageSize = (
  imageWidth: number,
  imageHeight: number
): { width: number; height: number } => {
  return calculateConstrainedImageSize(
    imageWidth,
    imageHeight,
    TEXT_BLOCK_IMAGE_MAX_SIZE,
    TEXT_BLOCK_IMAGE_MAX_SIZE
  );
};

const renderVideoUrl = (videoUrl?: string) => {
  const value = videoUrl?.trim();
  if (!value) return null;

  const text = (
    <Text style={PDFStyles.Text.detailVideoUrl}>
      {`Video URL: ${value}`}
    </Text>
  );
  const parsedVideoUrl = parseSupportedVideoUrl(value);

  return parsedVideoUrl
    ? <Link src={parsedVideoUrl.url.href} style={PDFStyles.Link.videoUrl}>{text}</Link>
    : text;
};

// WORK 타입 요소 렌더링
const renderWorkElement = (
  element: ProjectElementDataWithWork,
  getImageUrl: GetImageUrlFn,
  includeArtist: boolean,
) => {
  const { work, displaySize } = element;
  const showInfo = normalizePEInfoType(element.infoType) !== PEInfoType.NONE;

  if (!work || !work.uploadFile) {
    return null;
  }

  const imageWidth = work.uploadFile.width || 1;
  const imageHeight = work.uploadFile.height || 1;
  const { width: displayWidth, height: displayHeight } = calculateImageSize(
    imageWidth,
    imageHeight,
    displaySize || DisplaySize.BIG
  );

  // 정보 텍스트 렌더링 (VIDEO·PERFORMANCE일 경우 size 대신 duration 사용, WorkViewer와 동일)
  const renderInfo = () => {
    const useDurationCaption = usesWorkDuration(work.workType);
    const sizeString = convertWorkSizeToString(work.size, work.dimensionsType, work.sizeText);
    const captionSuffix = useDurationCaption ? (work.duration || '') : sizeString;
    const artistName = includeArtist ? resolveWorkArtistName(work.artist) : '';
    const oneLineText = [artistName, work.title, work.prodYear, work.material, captionSuffix]
      .filter(Boolean)
      .join(', ');
    const shouldStack = estimatePdfCaptionWidth(oneLineText) > displayWidth;

    return (
      <View style={[PDFStyles.Section.workInfoContainer, { width: displayWidth }]}>
        {shouldStack ? (
          <View style={PDFStyles.Section.workInfoStackedWrap}>
            <Text style={PDFStyles.Text.workInfoStacked}>
              {artistName ? `${artistName}, ` : ''}
              {work.title ? (
                <Text style={PDFStyles.Text.workTitleStacked}>{work.title}</Text>
              ) : null}
              {work.prodYear ? `${artistName || work.title ? ', ' : ''}${work.prodYear}` : ''}
            </Text>
            {!!work.material && (
              <Text style={PDFStyles.Text.workInfoStacked}>{work.material}</Text>
            )}
            {!!captionSuffix && (
              <Text style={PDFStyles.Text.workInfoStacked}>{captionSuffix}</Text>
            )}
          </View>
        ) : (
          <View style={{ flexDirection: 'row', alignItems: 'center', justifyContent: 'center', flexWrap: 'wrap' }}>
            {artistName && (
              <Text style={PDFStyles.Text.workInfoInline}>{artistName}</Text>
            )}
            {work.title && (
              <>
                {artistName && <Text style={PDFStyles.Text.workInfoInline}>, </Text>}
                <Text style={PDFStyles.Text.workTitleInline}>{work.title}</Text>
              </>
            )}
            {!!work.prodYear && (
              <Text style={PDFStyles.Text.workInfoInline}>
                {artistName || work.title ? ', ' : ''}{work.prodYear}
              </Text>
            )}
            {!!work.material && (
              <Text style={PDFStyles.Text.workInfoInline}>
                {artistName || work.title || work.prodYear ? ', ' : ''}{work.material}
              </Text>
            )}
            {!!captionSuffix && (
              <Text style={PDFStyles.Text.workInfoInline}>
                {artistName || work.title || work.prodYear || work.material ? ', ' : ''}{captionSuffix}
              </Text>
            )}
          </View>
        )}
        {useDurationCaption && renderVideoUrl(work.videoUrl)}
      </View>
    );
  };

  return (
    <View style={PDFStyles.Section.imageElementContainer}>
      <View style={PDFStyles.Section.imageWrapper}>
        <Image
          source={getImageUrl(work.uploadFile.originUrl)}
          style={[
            PDFStyles.Image.work,
            {
              width: displayWidth,
              height: displayHeight,
            }
          ]}
          // cache={true}
        />
      </View>
      {showInfo && renderInfo()}
      {/* Work description은 데이터만 유지하고 현재 PDF에는 노출하지 않음 */}
    </View>
  );
};

// Work description 노출을 다시 사용할 경우를 위해 렌더러는 유지한다.
const renderWorkDescription = (element: ProjectElementDataWithWork) => {
  const { work } = element;

  if (!work || !work.description) {
    return null;
  }

  return (
    <View style={PDFStyles.Section.workDescriptionPageContainer}>
      <View style={PDFStyles.Section.workDescriptionContainer}>
        <Text style={PDFStyles.Text.workDescription}>{work.description}</Text>
      </View>
    </View>
  );
};

// DETAIL 타입 요소 렌더링
const renderDetailElement = (element: ProjectElementDataWithDetail, getImageUrl: GetImageUrlFn) => {
  const { workDetail, displaySize } = element;
  const showInfo = normalizePEInfoType(element.infoType) !== PEInfoType.NONE;

  if (!workDetail || !workDetail.uploadFile) {
    return null;
  }

  const imageWidth = workDetail.uploadFile.width || 1;
  const imageHeight = workDetail.uploadFile.height || 1;
  const { width: displayWidth, height: displayHeight } = calculateImageSize(
    imageWidth,
    imageHeight,
    displaySize || DisplaySize.BIG
  );

  const hasDescription = !!workDetail.description;
  const hasVideoUrl = !!workDetail.videoUrl;

  return (
    <View style={PDFStyles.Section.imageElementContainer}>
      <View style={PDFStyles.Section.imageWrapper}>
        <Image
          source={getImageUrl(workDetail.uploadFile.originUrl)}
          style={[
            PDFStyles.Image.detail,
            {
              width: displayWidth,
              height: displayHeight,
            }
          ]}
          // cache={true}
        />
      </View>
      {showInfo && (hasDescription || hasVideoUrl) && (
        <View style={PDFStyles.Section.detailInfoContainer}>
          {hasDescription && (
            <Text style={PDFStyles.Text.detailDescription}>{workDetail.description}</Text>
          )}
          {hasVideoUrl && renderVideoUrl(workDetail.videoUrl)}
        </View>
      )}
    </View>
  );
};

// DOCUMENT 타입 요소 렌더링
const renderDocumentElement = (element: ProjectElementDataWithDocument, getImageUrl: GetImageUrlFn) => {
  const { document, displaySize } = element;
  const showInfo = normalizePEInfoType(element.infoType) !== PEInfoType.NONE;

  if (!document || !document.uploadFile) {
    return null;
  }

  const imageWidth = document.uploadFile.width || 1;
  const imageHeight = document.uploadFile.height || 1;
  const { width: displayWidth, height: displayHeight } = calculateImageSize(
    imageWidth,
    imageHeight,
    displaySize || DisplaySize.BIG
  );

  const hasDescription = !!document.description;
  const hasVideoUrl = !!document.videoUrl;

  return (
    <View style={PDFStyles.Section.imageElementContainer}>
      <View style={PDFStyles.Section.imageWrapper}>
        <Image
          source={getImageUrl(document.uploadFile.originUrl)}
          style={[
            PDFStyles.Image.document,
            {
              width: displayWidth,
              height: displayHeight,
            }
          ]}
          // cache={true}
        />
      </View>
      {showInfo && (hasDescription || hasVideoUrl) && (
        <View style={PDFStyles.Section.detailInfoContainer}>
          {hasDescription && (
            <Text style={PDFStyles.Text.detailDescription}>{document.description}</Text>
          )}
          {hasVideoUrl && renderVideoUrl(document.videoUrl)}
        </View>
      )}
    </View>
  );
};

// FORMATION 타입 요소 렌더링 (WIDE/BIG displaySize와 화면 그리드 비율 반영)
const renderFormationSlot = (
  item: FormationItemData,
  dimensions: FormationPdfSlotDimensions,
  getImageUrl: GetImageUrlFn,
) => {
  const resolved = resolveFormationSlotImage(item);
  const imageMode = getFormationImageMode(item);
  const imagePosition = getFormationImagePosition(item);

  return (
    <View
      key={item.id}
      style={[
        PDFStyles.Section.formationSlot,
        {
          width: dimensions.width,
          height: dimensions.height,
          backgroundColor: resolved?.originUrl && imageMode === FormationImageMode.FIT
            ? 'transparent'
            : '#ffffff',
        },
      ]}
    >
      {resolved?.originUrl && (
        <Image
          source={getImageUrl(resolved.originUrl)}
          style={[
            PDFStyles.Image.formationSlot,
            {
              objectFit: imageMode === FormationImageMode.FIT ? 'contain' : 'cover',
              objectPosition: `${imagePosition.xPercent}% ${imagePosition.yPercent}%`,
            },
          ]}
        />
      )}
    </View>
  );
};

const renderFormationElement = (
  element: ProjectElementDataWithFormation,
  getImageUrl: GetImageUrlFn,
  includeArtist: boolean,
) => {
  const { formation, displaySize } = element;

  if (!formation?.items?.length) {
    return null;
  }

  const contentWidth = A4_LANDSCAPE_WIDTH - PAGE_HORIZONTAL_PADDING;
  const layout = getFormationPdfLayout(formation.formationType, contentWidth, displaySize);
  const sortedItems = getVisibleFormationItems(formation.items, formation.formationType);
  const populatedItems = sortedItems.filter(
    (item) => resolveFormationSlotImage(item) !== null,
  );
  const hasVisibleFormationInfo =
    normalizePEInfoType(element.infoType) !== PEInfoType.NONE && populatedItems.length > 0;
  const infoPosition = getFormationPdfInfoPosition(
    sortedItems,
    layout,
    contentWidth,
    displaySize,
  );

  return (
    <View style={PDFStyles.Section.formationElementContainer}>
      <View
        style={[
          PDFStyles.Section.formationShell,
          {
            paddingHorizontal: layout.shellPaddingHorizontal,
            top: hasVisibleFormationInfo ? FORMATION_WITH_INFO_OFFSET_Y : 0,
          },
        ]}
      >
        <View
          style={[
            PDFStyles.Section.formationGrid,
            {
              width: layout.gridWidth,
              height: layout.gridHeight,
              gap: layout.gap,
            },
          ]}
        >
          {sortedItems.map((item, index) =>
            renderFormationSlot(
              item,
              layout.slots[index] ?? layout.slots[layout.slots.length - 1],
              getImageUrl,
            ),
          )}
        </View>
        {hasVisibleFormationInfo && (
          <View
            style={[
              PDFStyles.Section.formationInfoStack,
              {
                width: layout.gridWidth - infoPosition.paddingRight,
                marginTop: infoPosition.marginTop,
              },
            ]}
          >
            {populatedItems.map((item) => {
              const info = formatFormationItemInfo(item, includeArtist) || '—';
              return (
                <Text
                  key={item.id}
                  style={PDFStyles.Text.formationInfo}
                  maxLines={1}
                >
                  {`${item.slotIndex + 1}. ${info}`}
                </Text>
              );
            })}
          </View>
        )}
      </View>
    </View>
  );
};

// PDF 타입 요소 렌더링 (아이콘 + description.pdf, 클릭 시 원본 PDF URL)
const renderPdfElement = (element: ProjectElementDataWithPDF) => {
  const { pdfAsset } = element;

  if (!pdfAsset?.uploadFile?.originUrl) {
    return null;
  }

  const url = convertS3UrlToCloudFrontUrl(pdfAsset.uploadFile.originUrl);
  if (!url) {
    return null;
  }

  const description = pdfAsset.description || 'Open PDF';

  return (
    <View style={PDFStyles.Section.pdfAssetContainer}>
      <Link src={url} style={PDFStyles.Link.pdfAsset}>
        <View style={PDFStyles.Section.pdfAssetRow}>
          <Image source={pdfIcon} style={PDFStyles.Image.pdfIcon} />
          <View style={PDFStyles.Section.pdfNameRow}>
            <Text style={PDFStyles.Text.pdfName}>{description}</Text>
            <Text style={PDFStyles.Text.pdfExtension}>.pdf</Text>
          </View>
        </View>
      </Link>
    </View>
  );
};

// TEXTBOX 타입 요소 렌더링
const renderTextBoxElement = (element: ProjectElementDataWithTextBox) => {
  const { textBox } = element;

  if (!textBox || !textBox.content) {
    return null;
  }

  // 다른 projectElement와 조화를 이루도록 최대 너비 설정
  // WIDE가 85%이므로, TextBox는 약 70% 정도로 설정
  const availableWidth = A4_LANDSCAPE_WIDTH - PAGE_HORIZONTAL_PADDING; // 782pt
  const maxWidth = availableWidth * 0.70; // 약 547pt

  return (
    <View style={PDFStyles.Section.textBoxContainer}>
      <View style={[PDFStyles.Section.textBoxContent, { maxWidth }]}>
        {renderRichPdfText(textBox.content, PDFStyles.Text.textBoxContent)}
      </View>
    </View>
  );
};

// TEXT 타입 요소를 2단 "자동 흐름" 레이아웃으로 렌더링
const renderTextElement = (element: ProjectElementDataWithText, getImageUrl: GetImageUrlFn) => {
  const { selectedTextBlocks, text } = element;

  if (!selectedTextBlocks || selectedTextBlocks.length === 0) {
    return null;
  }

  const renderTextInfoInline = () => {
    const textInfoInlineText = `${text.title}${text.subtitle ? `-${text.subtitle}, ` : ", "}${text.source.author}, ${text.prodYear}`;
    return (
      <View style={PDFStyles.Section.textInfoInline}>
        <Text style={PDFStyles.Text.textInfoInlineText}>{textInfoInlineText}</Text>
      </View>
    );
  };

  const rightColumnBlocks: TextBlockData[] = [...selectedTextBlocks];

  return (
    <View style={PDFStyles.Section.textElementContainer}>
      <View style={PDFStyles.Section.twoColumnContainer}>
        <View style={PDFStyles.Section.column} />
        <View style={PDFStyles.Section.column}>
          {rightColumnBlocks.map((block, index) => (
            <View
              key={block.id || `right-${index}`}
              style={[
                PDFStyles.Section.textBlock,
                ...(index < rightColumnBlocks.length - 1
                  ? [PDFStyles.Section.textBlockGap]
                  : []),
              ]}
            >
              {renderTextBlock(block, getImageUrl)}
            </View>
          ))}
          {renderTextInfoInline()}
        </View>
      </View>
    </View>
  );
};

const DocumentProject: React.FC<DocumentProjectProps> = ({
  title,
  username,
  includeArtist,
  projectInfoList,
  projectElementList,
  resolvedImageUrlMap: resolvedImageUrlMapProp,
  resolvedGifFirstFrameMap: resolvedGifFirstFrameMapProp,
  includeCv = false,
  cvContentMode = AboutContentMode.Orig,
  cvMemberInfo = null,
  cvCareerList = [],
  careerSectionOrder,
}) => {
  const effectiveCvContentMode =
    cvContentMode === AboutContentMode.ENG && cvMemberInfo && cvMemberInfo.engVisible !== false
      ? AboutContentMode.ENG
      : AboutContentMode.Orig;

  const [imageUrlMap, setImageUrlMap] = useState<Record<string, string>>({});
  const [gifFirstFrameMap, setGifFirstFrameMap] = useState<Record<string, string>>({});

  const effectiveImageUrlMap = resolvedImageUrlMapProp ?? imageUrlMap;
  const effectiveGifFirstFrameMap = resolvedGifFirstFrameMapProp ?? gifFirstFrameMap;

  const getImageUrl: GetImageUrlFn = (originUrl: string) => {
    if (!originUrl) return originUrl;
    const baseUrl = convertS3UrlToCloudFrontUrl(originUrl);
    if (effectiveGifFirstFrameMap[baseUrl]) return effectiveGifFirstFrameMap[baseUrl];
    return effectiveImageUrlMap[baseUrl] || baseUrl;
  };

  const listKey = useMemo(
    () => projectElementList?.map((e) => e.id).join(',') ?? '',
    [projectElementList]
  );
  const projectElementListRef = useRef(projectElementList);
  projectElementListRef.current = projectElementList;

  useEffect(() => {
    if (resolvedImageUrlMapProp !== undefined && resolvedGifFirstFrameMapProp !== undefined) {
      return;
    }
    const list = projectElementListRef.current;
    if (!list || list.length === 0) {
      setImageUrlMap({});
      setGifFirstFrameMap({});
      return;
    }

    let cancelled = false;
    resolveProjectImageUrls(list).then(({ imageUrlMap: nextMap, gifFirstFrameMap: nextGifMap }) => {
      if (cancelled) return;
      setImageUrlMap(nextMap);
      setGifFirstFrameMap(nextGifMap);
    });
    return () => {
      cancelled = true;
    };
  }, [listKey, resolvedImageUrlMapProp, resolvedGifFirstFrameMapProp]);

  return (
    <Document>
      {/* 첫 페이지 */}
      <Page size="A4" orientation="landscape" style={PDFStyles.Page.root}>
        <View style={PDFStyles.Section.root}>
          {/* 상단: 프로젝트 제목 */}
          <View style={PDFStyles.Section.firstPageHeader}>
            <View style={PDFStyles.Section.titleContainer}>
              <Text style={PDFStyles.Text.title}>{title}</Text>
              <Text style={PDFStyles.Text.firstPageUsername}>{username}</Text>
            </View>
          </View>

          {/* 중간 공간: 이미지 위 영역 */}
          <View style={PDFStyles.Section.firstPageBottomSection}>
            {/* 프로젝트 정보: 이미지 위, 왼쪽 정렬 */}
            {projectInfoList && projectInfoList.length > 0 && (
              <View style={PDFStyles.Section.projectInfoContainer}>
                {projectInfoList.map((info, index) => (
                  <View key={info.id || index} style={PDFStyles.Section.infoRow}>
                    <Text style={PDFStyles.Text.infoLabel}>{getProjectInfoDisplayName(info.customName)}</Text>
                    <View style={PDFStyles.Section.infoValueContainer}>
                      {info.customValues.filter((value) => value.text.trim() !== '').map((value, valueIndex) => (
                        <Text key={valueIndex} style={PDFStyles.Text.infoValue}>{value.text}</Text>
                      ))}
                    </View>
                  </View>
                ))}
              </View>
            )}
          </View>
        </View>

        <View style={PDFStyles.Section.pageNumber} fixed>
          <Text
            style={PDFStyles.Text.pageNumber}
            render={({ pageNumber, totalPages }: { pageNumber: number; totalPages: number }) =>
              `${pageNumber} / ${totalPages}`
            }
          />
        </View>
      </Page>

      {/* 각 projectElement마다 페이지 생성 */}
      {projectElementList &&
        projectElementList.flatMap((element, index) => {
          // divider는 무시
          if (element.projectElementType === ProjectElementType.DIVIDER) {
            return [];
          }

          // PDF는 URL 없으면 무시
          if (element.projectElementType === ProjectElementType.PDF) {
            const pdfAsset = (element as ProjectElementDataWithPDF).pdfAsset;
            const originUrl = pdfAsset?.uploadFile?.originUrl;
            if (!originUrl || !convertS3UrlToCloudFrontUrl(originUrl)) {
              return [];
            }
          }

          const pages = [];

          // 기본 element 페이지
          pages.push(
            <Page
              key={element.id || `element-${index}`}
              size="A4"
              orientation="landscape"
              style={PDFStyles.Page.root}
            >
              <View style={PDFStyles.Section.root}>
                <View style={PDFStyles.Section.elementContainer}>
                  {element.projectElementType === ProjectElementType.WORK && (
                    <View style={PDFStyles.Section.elementContent}>
                      {renderWorkElement(
                        element as ProjectElementDataWithWork,
                        getImageUrl,
                        includeArtist,
                      )}
                    </View>
                  )}
                  {element.projectElementType === ProjectElementType.DETAIL && (
                    <View style={PDFStyles.Section.elementContent}>
                      {renderDetailElement(element as ProjectElementDataWithDetail, getImageUrl)}
                    </View>
                  )}
                  {element.projectElementType === ProjectElementType.DOCUMENT && (
                    <View style={PDFStyles.Section.elementContent}>
                      {renderDocumentElement(element as ProjectElementDataWithDocument, getImageUrl)}
                    </View>
                  )}
                  {element.projectElementType === ProjectElementType.TEXT && (
                    <View style={PDFStyles.Section.elementContent}>
                      {renderTextElement(element as ProjectElementDataWithText, getImageUrl)}
                    </View>
                  )}
                  {element.projectElementType === ProjectElementType.TEXTBOX && (
                    <View style={PDFStyles.Section.elementContent}>
                      {renderTextBoxElement(element as ProjectElementDataWithTextBox)}
                    </View>
                  )}
                  {element.projectElementType === ProjectElementType.PDF && (
                    <View style={PDFStyles.Section.elementContent}>
                      {renderPdfElement(element as ProjectElementDataWithPDF)}
                    </View>
                  )}
                  {element.projectElementType === ProjectElementType.FORMATION && (
                    <View style={PDFStyles.Section.elementContent}>
                      {renderFormationElement(
                        element as ProjectElementDataWithFormation,
                        getImageUrl,
                        includeArtist,
                      )}
                    </View>
                  )}
                </View>
              </View>

              {/* 왼쪽 위에 프로젝트 title 표시 */}
              <View style={PDFStyles.Section.pageTitle} fixed>
                <Text style={PDFStyles.Text.pageTitle}>{title}</Text>
              </View>

              {/* 오른쪽 위에 logo 표시 */}
              <View style={PDFStyles.Section.pageLogo} fixed>
                <Image
                  source={logo}
                  style={PDFStyles.Image.logo}
                  cache={true}
                />
              </View>

              {/* 왼쪽 아래에 username 표시 */}
              <View style={PDFStyles.Section.pageUsername} fixed>
                <Text style={PDFStyles.Text.pageUsername}>{username}</Text>
              </View>

              <View style={PDFStyles.Section.pageNumber} fixed>
                <Text
                  style={PDFStyles.Text.pageNumber}
                  render={({ pageNumber, totalPages }: { pageNumber: number; totalPages: number }) =>
                    `${pageNumber} / ${totalPages}`
                  }
                />
              </View>
            </Page>
          );

          return pages;
        })}

      {includeCv && cvMemberInfo && (
        <HorizontalCVPdfPage
          memberInfo={cvMemberInfo}
          careerList={(cvCareerList ?? []).filter((c) => c.isPublic)}
          careerSectionOrder={careerSectionOrder}
          contentMode={effectiveCvContentMode}
          logoDocumentFixed={false}
          pageNumberDocumentFixed={true}
        />
      )}
    </Document>
  );
};

// Typography
const PDFTypography = {
  Title: {
    fontSize: 14,
    fontFamily: 'NotoKR',
    fontWeight: 600,
  },
  FirstPageUsername: {
    fontSize: 12,
    fontFamily: 'NotoKR',
    fontWeight: 400,
  },
  InfoLabel: {
    fontSize: 9,
    fontFamily: 'NotoKR',
    fontWeight: 500,
  },
  InfoValue: {
    fontSize: 9,
    fontFamily: 'NotoKR',
    fontWeight: 400,
  },
  PageNumber: {
    fontSize: 7,
    fontFamily: 'NotoKR',
    fontWeight: 400,
  },
  PageTitle: {
    fontSize: 6,
    fontFamily: 'NotoKR',
    fontWeight: 400,
  },
  PageUsername: {
    fontSize: 6,
    fontFamily: 'NotoKR',
    fontWeight: 400,
  },
  ElementPlaceholder: {
    fontSize: 12,
    fontFamily: 'NotoKR',
    fontWeight: 400,
  },
  TextParagraph: {
    fontSize: TEXT_PARAGRAPH_FONT_SIZE,
    fontFamily: 'NotoKR',
    fontWeight: 400,
    lineHeight: 1.6,
    marginBottom: 0,
  },
  TextLink: {
    fontSize: 9,
    fontFamily: 'NotoKR',
    fontWeight: 400,
    lineHeight: 1.6,
    marginBottom: 8,
  },
  TextTitle: {
    fontSize: 10,
    fontFamily: 'NotoKR',
    fontWeight: 500,
    marginBottom: 0,
  },
  TextSubtitle: {
    fontSize: 8,
    fontFamily: 'NotoKR',
    fontWeight: 400,
    marginBottom: 4,
  },
  TextAuthor: {
    fontSize: 8,
    fontFamily: 'NotoKR',
    fontWeight: 400,
  },
  TextInfoInline: {
    fontSize: 7,
    fontFamily: 'NotoKR',
    fontWeight: 400,
  },
  WorkInfoInline: {
    fontSize: 8,
    fontFamily: 'NotoKR',
    fontWeight: 400,
    textAlign: 'center' as const,
  },
  WorkTitleInline: {
    fontSize: 8,
    fontFamily: 'NotoKR',
    fontWeight: 400,
    textAlign: 'center' as const,
    transform: 'skewX(-12deg)',
  },
  WorkTitle: {
    fontSize: 8,
    fontFamily: 'NotoKR',
    fontWeight: 400,
    transform: 'skewX(-12deg)',
    textAlign: 'center' as const,
  },
  WorkInfoStacked: {
    fontSize: 8,
    fontFamily: 'NotoKR',
    fontWeight: 400,
    textAlign: 'left' as const,
    width: '100%',
  },
  WorkTitleStacked: {
    fontSize: 8,
    fontFamily: 'NotoKR',
    fontWeight: 400,
    transform: 'skewX(-12deg)',
  },
  WorkDescription: {
    fontSize: 8,
    fontFamily: 'NotoKR',
    fontWeight: 400,
    lineHeight: 1.6,
    borderLeftWidth: 0.5,
    borderLeftColor: '#000',
    borderLeftStyle: 'solid' as const,
    paddingLeft: 10,
    paddingTop: 15,
  },
  DetailDescription: {
    fontSize: 8,
    fontFamily: 'NotoKR',
    fontWeight: 400,
    textAlign: 'center' as const,
    lineHeight: 1.5,
    transform: 'skewX(-12deg)',
  },
  DetailVideoUrl: {
    fontSize: 7,
    fontFamily: 'NotoKR',
    fontWeight: 400,
    textAlign: 'center' as const,
    lineHeight: 1.5,
    transform: 'skewX(-12deg)',
  },
  TextBlockImageCaption: {
    fontSize: 6,
    fontFamily: 'NotoKR',
    fontWeight: 400,
    textAlign: 'left' as const,
    lineHeight: 1.4,
    color: '#666',
  },
  TextBoxContent: {
    fontSize: 10,
    fontFamily: 'NotoKR',
    fontWeight: 400,
    lineHeight: 1.4,
  },
  FormationInfo: {
    fontSize: 7,
    fontFamily: 'NotoKR',
    fontWeight: 400,
    color: '#7d7d7d',
    textAlign: 'right' as const,
    lineHeight: 1.2,
  },
  PdfName: {
    fontSize: 10,
    fontFamily: 'NotoKR',
    fontWeight: 400,
    color: '#000',
    transform: 'skewX(-12deg)',
  },
  PdfExtension: {
    fontSize: 10,
    fontFamily: 'NotoKR',
    fontWeight: 400,
    color: '#000',
    transform: 'skewX(-12deg)',
  },
};

const PDFStyles = {
  Link: StyleSheet.create({
    pdfAsset: {
      color: '#000',
      textDecoration: 'none',
    },
    videoUrl: {
      color: '#000',
      textDecoration: 'underline',
    },
    textBlock: {
      color: '#000',
      textDecoration: 'underline',
    },
  }),

  Page: StyleSheet.create({
    root: {
      paddingTop: 30,
      paddingBottom: 30,
      paddingLeft: 30,
      paddingRight: 30,
      backgroundColor: '#ffffff',
      fontFamily: 'NotoKR',
      fontWeight: 400,
    }
  }),

  Image: StyleSheet.create({
    work: {
      objectFit: 'contain' as const,
      borderWidth: 0.5,
      borderColor: '#000',
      borderStyle: 'solid' as const,
    },
    detail: {
      objectFit: 'contain' as const,
      borderWidth: 0.5,
      borderColor: '#000',
      borderStyle: 'solid' as const,
    },
    document: {
      objectFit: 'contain' as const,
      borderWidth: 0.5,
      borderColor: '#000',
      borderStyle: 'solid' as const,
    },
    textBlockImage: {
      objectFit: 'contain' as const,
    },
    logo: {
      width: 24,
      height: 24,
      objectFit: 'contain' as const,
    },
    pdfIcon: {
      width: 14,
      height: 14,
      objectFit: 'contain' as const,
    },
    formationSlot: {
      width: '100%',
      height: '100%',
      objectFit: 'cover' as const,
    },
  }),

  Section: StyleSheet.create({
    root: {
      flexGrow: 1,
    },
    firstPageHeader: {
      width: '100%',
      marginBottom: 10,
    },
    titleContainer: {
      width: '75%', // A4 width(842pt)의 3/4
    },
    firstPageBottomSection: {
      flexGrow: 1,
      justifyContent: 'flex-end',
      marginBottom: 0,
    },
    projectInfoContainer: {
      width: '100%',
      marginBottom: 0,
      alignItems: 'flex-start',
      borderLeftWidth: 0.5,
      borderLeftColor: '#000',
      borderLeftStyle: 'solid' as const,
      paddingLeft: 10,
    },
    infoContainer: {
      flex: 1,
      paddingLeft: 20,
    },
    infoRow: {
      flexDirection: 'row',
      marginBottom: 6,
      alignItems: 'flex-start',
      width: '100%',
    },
    infoValueContainer: {
      flex: 1,
      minWidth: 0,
      gap: 2,
    },
    elementContainer: {
      flexGrow: 1,
      width: '100%',
    },
    elementContent: {
      width: '100%',
      flexGrow: 1,
    },

    // header / body / footer 구조를 안정적으로 만들기 위해 flex 사용
    textElementContainer: {
      width: '100%',
      flexGrow: 1,
      flexDirection: 'column',
      paddingTop: 30,
    },
    textHeader: {
      width: '100%',
      marginBottom: 20,
      flexShrink: 0,
      // 왼쪽 컬럼 너비에 맞춤 (columnWidth와 동일)
    },
    textAuthor: {
      width: '100%',
      alignItems: 'flex-end',
      marginTop: 0,
    },
    textInfoInline: {
      width: '100%',
      alignItems: 'flex-end',
      marginTop: 6,
      flexShrink: 0,
    },

    /**
     * 방법 A: 두 개의 명시적 컬럼 컨테이너
     * - flexDirection: 'row'로 왼쪽/오른쪽 컬럼을 나란히 배치
     */
    twoColumnContainer: {
      flexGrow: 1,
      flexShrink: 1,
      width: '100%',
      flexDirection: 'row',
      overflow: 'hidden',
    },

    /**
     * 각 컬럼 (왼쪽/오른쪽)
     * - flex: 1로 동일한 너비 유지
     * - flexDirection: 'column'으로 텍스트 블록들을 세로로 배치
     */
    column: {
      flex: 1,
      flexDirection: 'column',
      paddingLeft: 15,
      paddingRight: 15,
    },

    textBlock: {
      width: '100%',
      marginBottom: 0,
    },
    textBlockGap: {
      marginBottom: TEXT_BLOCK_GAP,
    },
    textBlockImageContainer: {
      width: '100%',
      alignItems: 'center',
      marginBottom: 20,
      flexShrink: 0,
    },
    textBlockImageWrapper: {
      width: '100%',
      alignItems: 'center',
      justifyContent: 'center',
      marginBottom: 0,
      flexShrink: 0,
    },
    // 이미지 요소 컨테이너 (WORK, DETAIL, DOCUMENT)
    imageElementContainer: {
      width: '100%',
      flexGrow: 1,
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
    },
    imageWrapper: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      marginBottom: 4,
    },
    workInfoContainer: {
      width: '100%',
      alignItems: 'center',
      justifyContent: 'center',
    },
    workInfoStackedWrap: {
      width: '100%',
      alignItems: 'flex-start',
    },
    workDescriptionPageContainer: {
      width: '100%',
      flexGrow: 1,
      flexDirection: 'column',
      alignItems: 'flex-start',
      justifyContent: 'flex-start',
      paddingTop: 0,
    },
    workDescriptionContainer: {
      width: 280,
      alignItems: 'flex-start',
    },
    detailInfoContainer: {
      width: '100%',
      alignItems: 'center',
      justifyContent: 'center',
    },

    // TextBox 컨테이너 (중앙 배치)
    textBoxContainer: {
      width: '100%',
      flexGrow: 1,
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
    },
    pdfAssetContainer: {
      width: '100%',
      flexGrow: 1,
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
    },
    formationElementContainer: {
      width: '100%',
      flexGrow: 1,
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
    },
    formationShell: {
      width: '100%',
      flexDirection: 'column',
      alignItems: 'center',
    },
    formationGrid: {
      flexDirection: 'row',
    },
    formationInfoStack: {
      alignSelf: 'flex-start',
      alignItems: 'flex-end',
      gap: FORMATION_INFO_ROW_GAP,
      flexShrink: 0,
    },
    formationSlot: {
      overflow: 'hidden',
      backgroundColor: '#ffffff',
    },
    pdfAssetRow: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: 6,
    },
    pdfNameRow: {
      flexDirection: 'row',
      alignItems: 'flex-end',
    },
    textBoxContent: {
      width: '100%',
      paddingHorizontal: 20,
    },

    pageTitle: {
      position: 'absolute',
      top: 10,
      left: 20,
    },
    pageLogo: {
      position: 'absolute',
      top: 5,
      right: 16,
    },
    pageUsername: {
      position: 'absolute',
      top: 10,
      left: 0,
      right: 0,
      textAlign: 'center',
    },
    pageNumber: {
      position: 'absolute',
      bottom: 15,
      left: 0,
      right: 20,
      textAlign: 'right',
    },
  }),

  Text: StyleSheet.create({
    title: {
      ...PDFTypography.Title,
      marginBottom: 4,
    },
    infoLabel: {
      ...PDFTypography.InfoLabel,
      width: 120,
      marginRight: 10,
    },
    infoValue: {
      ...PDFTypography.InfoValue,
    },
    pageNumber: {
      ...PDFTypography.PageNumber,
    },
    pageTitle: {
      ...PDFTypography.PageTitle,
    },
    pageUsername: {
      ...PDFTypography.PageUsername,
    },
    firstPageUsername: {
      ...PDFTypography.FirstPageUsername,
    },
    elementPlaceholder: {
      ...PDFTypography.ElementPlaceholder,
    },
    textParagraph: {
      ...PDFTypography.TextParagraph,
    },
    textLink: {
      ...PDFTypography.TextLink,
    },
    textTitle: {
      ...PDFTypography.TextTitle,
    },
    textSubtitle: {
      ...PDFTypography.TextSubtitle,
    },
    textAuthorText: {
      ...PDFTypography.TextAuthor,
    },
    textInfoInlineText: {
      ...PDFTypography.TextInfoInline,
    },
    workInfoInline: {
      ...PDFTypography.WorkInfoInline,
    },
    workTitleInline: {
      ...PDFTypography.WorkTitleInline,
    },
    workTitle: {
      ...PDFTypography.WorkTitle,
    },
    workInfoStacked: {
      ...PDFTypography.WorkInfoStacked,
    },
    workTitleStacked: {
      ...PDFTypography.WorkTitleStacked,
    },
    workDescription: {
      ...PDFTypography.WorkDescription,
    },
    detailDescription: {
      ...PDFTypography.DetailDescription,
    },
    detailVideoUrl: {
      ...PDFTypography.DetailVideoUrl,
      marginTop: 2,
    },
    textBlockImageCaption: {
      ...PDFTypography.TextBlockImageCaption,
    },
    textBoxContent: {
      ...PDFTypography.TextBoxContent,
    },
    formationInfo: {
      ...PDFTypography.FormationInfo,
      width: '100%',
    },
    pdfName: {
      ...PDFTypography.PdfName,
      borderBottomWidth: 0.5,
      borderBottomColor: '#999',
      borderBottomStyle: 'solid' as const,
    },
    pdfExtension: {
      ...PDFTypography.PdfExtension,
      paddingLeft: 1,
      borderBottomWidth: 0.5,
      borderBottomColor: '#999',
      borderBottomStyle: 'solid' as const,
    },
  }),
};

export default DocumentProject;
